import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="secure-page"
export default class extends Controller {
  static values = {
    disablePrint: Boolean,
    disableSelection: Boolean,
    disableRightClick: Boolean,
    disableScreenshot: Boolean,
    watermark: Boolean,
    publicPortal: Boolean
  }

  connect() {
    this.setupSecurityFeatures()
    
    if (this.watermarkValue) {
      this.addWatermark()
    }
  }

  disconnect() {
    this.removeSecurityFeatures()
  }

  setupSecurityFeatures() {
    // Disable print
    if (this.disablePrintValue) {
      this.disablePrint()
    }

    // Disable text selection
    if (this.disableSelectionValue) {
      this.disableTextSelection()
    }

    // Disable right-click context menu
    if (this.disableRightClickValue) {
      this.disableContextMenu()
    }

    // Attempt to prevent screenshots (limited effectiveness)
    if (this.disableScreenshotValue) {
      this.preventScreenshots()
    }

    // Detect developer tools (skip on the public portal — the mobile on-screen
    // keyboard changes window.innerHeight enough to false-trigger this heuristic)
    if (!this.publicPortalValue) {
      this.detectDevTools()
    }

    // Add CSS to hide content when printing
    this.addPrintStyles()
  }

  disablePrint() {
    // Intercept Ctrl+P / Cmd+P
    this.printHandler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault()
        this.showSecurityWarning('Printing is disabled for this sensitive content', 'print')
        return false
      }
    }
    document.addEventListener('keydown', this.printHandler)

    // Intercept print event
    this.beforePrintHandler = (e) => {
      e.preventDefault()
      this.showSecurityWarning('Printing is disabled for this sensitive content')
      return false
    }
    window.addEventListener('beforeprint', this.beforePrintHandler)
  }

  disableTextSelection() {
    // CSS-based approach
    this.element.style.userSelect = 'none'
    this.element.style.webkitUserSelect = 'none'
    this.element.style.mozUserSelect = 'none'
    this.element.style.msUserSelect = 'none'

    // JavaScript-based approach
    this.selectStartHandler = (e) => {
      // Check if the event originated from a secure-exempt element
      if (e.target.closest('.secure-exempt')) {
        return true // Allow selection for exempt elements
      }

      // On the public portal, also allow selection inside native form controls
      // (typing/selecting in inputs must never be blocked there). Auth-bound
      // pages (e.g. law_enforcement) keep their exact prior strict behavior.
      if (this.publicPortalValue && e.target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]')) {
        return true
      }

      e.preventDefault()
      return false
    }
    this.element.addEventListener('selectstart', this.selectStartHandler)

    // Disable copy (except for secure-exempt elements)
    this.copyHandler = (e) => {
      // Check if the event originated from a secure-exempt element
      if (e.target.closest('.secure-exempt')) {
        return true // Allow copy for exempt elements
      }

      // On the public portal, also allow copy inside native form controls.
      // Auth-bound pages keep their exact prior strict behavior.
      if (this.publicPortalValue && e.target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]')) {
        return true
      }

      e.clipboardData.setData('text/plain', 'Copying is disabled for sensitive content')
      e.preventDefault()
      this.showSecurityWarning('Copying is disabled for this sensitive content', 'copy')
      return false
    }
    document.addEventListener('copy', this.copyHandler)

    // Disable cut
    this.cutHandler = (e) => {
      e.preventDefault()
      this.showSecurityWarning('Cutting is disabled for this sensitive content', 'cut')
      return false
    }
    document.addEventListener('cut', this.cutHandler)
  }

  disableContextMenu() {
    this.contextMenuHandler = (e) => {
      // Check if the event originated from a secure-exempt element
      if (e.target.closest('.secure-exempt')) {
        return true // Allow right-click for exempt elements
      }
      
      e.preventDefault()
      this.showSecurityWarning('Right-click is disabled for this sensitive content', 'right_click')
      return false
    }
    this.element.addEventListener('contextmenu', this.contextMenuHandler)
  }

  preventScreenshots() {
    // Note: This has limited effectiveness and mainly works on some mobile browsers
    // Desktop screenshot tools bypass this entirely
    
    // Detect PrintScreen key (limited effectiveness)
    this.screenshotHandler = (e) => {
      if (e.key === 'PrintScreen') {
        navigator.clipboard.writeText('')
        this.showSecurityWarning('Screenshots are prohibited for this sensitive content', 'screenshot')
      }
    }
    document.addEventListener('keyup', this.screenshotHandler)

    // Blur content when window loses focus (could indicate screenshot).
    // Skipped on the public portal — mobile tab/app switches fire window
    // blur constantly and this would blur the page on every switch.
    if (!this.publicPortalValue) {
      this.blurHandler = () => {
        this.element.style.filter = 'blur(10px)'
      }
      this.focusHandler = () => {
        this.element.style.filter = 'none'
      }

      window.addEventListener('blur', this.blurHandler)
      window.addEventListener('focus', this.focusHandler)
    }
  }

  detectDevTools() {
    let devtools = { open: false, orientation: null }
    const threshold = 160
    
    this.devToolsInterval = setInterval(() => {
      if (window.outerHeight - window.innerHeight > threshold || 
          window.outerWidth - window.innerWidth > threshold) {
        if (!devtools.open) {
          devtools.open = true
          this.onDevToolsOpen()
        }
      } else {
        if (devtools.open) {
          devtools.open = false
          this.onDevToolsClose()
        }
      }
    }, 500)
  }

  onDevToolsOpen() {
    console.warn('Developer tools detected - sensitive content protection active')
    // Could blur content or show warning
    this.element.classList.add('devtools-open')
  }

  onDevToolsClose() {
    this.element.classList.remove('devtools-open')
  }

  addPrintStyles() {
    if (!document.getElementById('secure-print-styles')) {
      const style = document.createElement('style')
      style.id = 'secure-print-styles'
      style.innerHTML = `
        @media print {
          [data-controller~="secure-page"] {
            display: none !important;
          }

          body::before {
            content: "This content is classified and cannot be printed.";
            display: block;
            text-align: center;
            padding: 50px;
            font-size: 24px;
            font-weight: bold;
            color: #dc2626;
          }
        }

        /* Allow selection and interaction for secure-exempt elements */
        .secure-exempt, .secure-exempt * {
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
        }
      `
      document.head.appendChild(style)
    }

    // Public-portal-only relaxation, kept in its own stylesheet so it can never
    // apply to auth-bound pages (e.g. law_enforcement) regardless of page load
    // order: typing/selecting in native form controls must never be blocked
    // on the public portal.
    if (this.publicPortalValue && !document.getElementById('secure-print-styles-public-portal-exempt')) {
      const publicPortalStyle = document.createElement('style')
      publicPortalStyle.id = 'secure-print-styles-public-portal-exempt'
      publicPortalStyle.innerHTML = `
        input, textarea, select, [contenteditable="true"], [contenteditable=""] {
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
        }
      `
      document.head.appendChild(publicPortalStyle)
    }
  }

  addWatermark() {
    const watermarkDiv = document.createElement('div')
    watermarkDiv.className = 'security-watermark'
    watermarkDiv.innerHTML = `
      <div class="watermark-text">
        CONFIDENTIAL - LAW ENFORCEMENT ONLY<br>
        ${new Date().toLocaleString()}<br>
        User: ${this.element.dataset.currentUser || 'Unknown'}
      </div>
    `
    
    // Add CSS for watermark
    if (!document.getElementById('watermark-styles')) {
      const style = document.createElement('style')
      style.id = 'watermark-styles'
      style.innerHTML = `
        .security-watermark {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 9999;
          opacity: 0.1;
          user-select: none;
        }
        
        .watermark-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 48px;
          font-weight: bold;
          color: #dc2626;
          text-align: center;
          white-space: nowrap;
        }
      `
      document.head.appendChild(style)
    }
    
    this.element.appendChild(watermarkDiv)
    this.watermarkElement = watermarkDiv
  }

  showSecurityWarning(message, actionType) {
    // Create or update warning element
    let warning = document.getElementById('security-warning')
    if (!warning) {
      warning = document.createElement('div')
      warning.id = 'security-warning'
      warning.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc2626;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        font-weight: 600;
        z-index: 10000;
        animation: slideIn 0.3s ease-out;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      `
      document.body.appendChild(warning)

      // Add animation
      if (!document.getElementById('warning-animation')) {
        const style = document.createElement('style')
        style.id = 'warning-animation'
        style.innerHTML = `
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `
        document.head.appendChild(style)
      }
    }

    warning.textContent = message
    warning.style.display = 'block'

    // Auto-hide after 3 seconds
    clearTimeout(this.warningTimeout)
    this.warningTimeout = setTimeout(() => {
      warning.style.display = 'none'
    }, 3000)

    // Log security event with proper action type. Skipped on the public portal:
    // it is anonymous (no session to audit) and any logged-in staff browsing the
    // portal must never risk account lockout or a form-wiping reload from this.
    if (!this.publicPortalValue) {
      this.logSecurityEvent(actionType || 'security_violation')
    }
  }

  logSecurityEvent(actionType) {
    // Send to server for audit logging
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch('/api/v1/security_events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        event_type: 'security_action_blocked',
        violation_action: actionType,
        page: window.location.pathname,
        timestamp: new Date().toISOString()
      })
    }).then(response => response.json())
    .then(data => {
      if (data.status === 'account_locked') {
        // Account has been locked, force logout
        // First, sign out via Devise endpoint to clear server session
        fetch('/users/sign_out', {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).finally(() => {
          // Clear local storage and session storage
          localStorage.clear()
          sessionStorage.clear()
          
          // Show alert before redirect
          alert('Your account has been locked due to security violations. Please contact your administrator.')
          
          // Force redirect to sign in page
          window.location.replace('/users/sign_in')
        })
      } else if (data.status === 'warning') {
        // Reload the page to display the Rails flash message
        // The message is stored in the session and will be displayed by the Flash::ToastComponent
        window.location.reload()
      }
    })
    .catch(() => {
      // Silently fail - don't break functionality
    })
  }

  removeSecurityFeatures() {
    // Clean up event listeners
    if (this.printHandler) {
      document.removeEventListener('keydown', this.printHandler)
    }
    if (this.beforePrintHandler) {
      window.removeEventListener('beforeprint', this.beforePrintHandler)
    }
    if (this.selectStartHandler) {
      this.element.removeEventListener('selectstart', this.selectStartHandler)
    }
    if (this.copyHandler) {
      document.removeEventListener('copy', this.copyHandler)
    }
    if (this.cutHandler) {
      document.removeEventListener('cut', this.cutHandler)
    }
    if (this.contextMenuHandler) {
      this.element.removeEventListener('contextmenu', this.contextMenuHandler)
    }
    if (this.screenshotHandler) {
      document.removeEventListener('keyup', this.screenshotHandler)
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler)
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler)
    }
    if (this.devToolsInterval) {
      clearInterval(this.devToolsInterval)
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout)
    }
    if (this.watermarkElement) {
      this.watermarkElement.remove()
    }

    // Reset styles
    this.element.style.userSelect = ''
    this.element.style.webkitUserSelect = ''
    this.element.style.mozUserSelect = ''
    this.element.style.msUserSelect = ''
    this.element.style.filter = ''
  }
}